import { flatten } from "@/plugins/helpers";
import router from "@/router";
import AccountApi from "@/services/account.api";
import AuthApi from "@/services/auth.api";
import OrderApi from "@/services/order.api";
import TrainplanetApi from "@/services/trainplanet.api";
import { OrderLockException } from "@/services/trainplanet.exceptions";
import moment from "moment";
import Cookies from "js-cookie";

const vuex = {};

const commitOrder = (commit, order) => {
  commit("setOrder", order);
  if (order.tenant) {
    commit("setTenant", order.tenant, { root: true });
  }
};

const getPaymentSessionUrls = ({ orderId, token }) => {
  const checkout = router.resolve({
    name: "pay",
    params: {
      orderId,
      token,
    },
  });
  const confirmation = router.resolve({
    name: "confirmation",
    params: {
      orderId,
      token,
    },
  });

  return {
    checkoutUrl: window.location.origin + checkout.href,
    confirmationUrl: window.location.origin + confirmation.href,
  };
};

vuex.state = {
  loading: false,
  loadingLockOwner: null,
  error: null,
  errorCode: null,
  fetchOrder: null,
  orderId: null,
  tempMail: "",
  token: null,
  offerMode: false,
  data: {
    customer: null,
    tenant: null,
  },
  itemId: null,
  paymentSession: null,
  kco: null,
  kcoVersion: 0,
  isOrderLock: false,
  orderLocks: JSON.parse(localStorage.getItem("orderLocks")) || {},
  orderLockSchedule: null,
  addedTaxis: null,
  swishSession: null,
  swishPaymentInProgress: false,
  selectedPaymentType: null,
  hasPrintableTickets: false,
  klarnaSessionLoading: false,
  loginModalVisible: false,
  registerModalVisible: false,
};

vuex.getters = {
  offerMode: (state) => state.offerMode,
  loading: (state) => state.loading,
  loadingLockOwner: (state) => state.loadingLockOwner,
  tempMail: (state) => state.tempMail,
  error: (state) => state.error,
  errorCode: (state) => state.errorCode,
  api: (state) => new TrainplanetApi(state.orderId, state.token),
  authApi: (state) => new AuthApi(state.orderId, state.token),
  accountApi: (state) => new AccountApi(state.orderId, state.token),
  orderApi: (state) => new OrderApi(state.orderId, state.token),
  data: (state) => state.data,
  orderId: (state) => state.orderId,
  itemId: (state) => state.itemId,
  orderLockExpiry: (state) => state.orderLocks[state.orderId],
  paymentSession: (state) => state.paymentSession,
  swishSession: (state) => state.swishSession,
  swishPaymentInProgress: (state) => state.swishPaymentInProgress,
  selectedPaymentType: (state) => state.selectedPaymentType,
  hasPrintableTickets: (state) => state.hasPrintableTickets,
  klarnaSessionLoading: (state) => state.klarnaSessionLoading,
  loginModalVisible: (state) => state.loginModalVisible,
  registerModalVisible: (state) => state.registerModalVisible,
  isCustomerValid: (state) => {
    let { customer } = state.data;

    // Non required values
    delete customer.billing;
    delete customer.delivery.address2;
    delete customer.delivery.region;

    // Validate
    customer = flatten(customer);

    return !Object.values(customer).some((x) => x == null || x === "");
  },
  kco: (state) => state.kco,
  kcoVersion: (state) => state.kcoVersion,
};

vuex.mutations = {
  setLoginModalVisible: (state, value) => {
    state.loginModalVisible = value;
  },
  setRegisterModalVisible: (state, value) => {
    state.registerModalVisible = value;
  },
  setOfferMode: (state, value) => {
    state.offerMode = value;
  },
  setTempMail: (state, value) => {
    state.tempMail = value;
  },
  setLoading: (state, payload) => {
    const isObjectPayload =
      payload !== null &&
      typeof payload === "object" &&
      !Array.isArray(payload);
    const value = isObjectPayload ? payload.value : payload;
    const source = isObjectPayload ? payload.source || null : null;
    const currentRouteName = router?.currentRoute?.value?.name || null;

    if (value === true && currentRouteName === "confirmation") {
      return;
    }

    if (
      value === false &&
      state.loadingLockOwner &&
      state.loadingLockOwner !== source
    ) {
      return;
    }

    state.loading = value;
  },
  setLoadingLockOwner: (state, owner) => {
    state.loadingLockOwner = owner || null;
  },
  clearLoadingLockOwner: (state, owner = null) => {
    if (!owner || state.loadingLockOwner === owner) {
      state.loadingLockOwner = null;
    }
  },
  setError: (state, error) => {
    state.error = error;
  },
  setErrorCode: (state, errorCode) => {
    state.errorCode = errorCode;
  },
  setOrderId: (state, orderId) => {
    state.orderId = orderId;
  },

  setToken: (state, token) => {
    state.token = token;
  },
  setOrder: (state, order) => {
    state.data = order;
  },

  setPaymentSession: (state, session) => {
    state.paymentSession = session;
  },
  orderLocked: (state, expires) => {
    state.orderLocks[state.orderId] = expires;
    localStorage.setItem("orderLocks", JSON.stringify(state.orderLocks));
  },
  orderUnlocked: (state) => {
    delete state.orderLocks[state.orderId];
    localStorage.setItem("orderLocks", JSON.stringify(state.orderLocks));
  },
  lockOrder: (state) => {
    state.isOrderLock = true;
  },
  unlockOrder: (state) => {
    state.isOrderLock = false;
  },
  setKco: (state, kco) => {
    state.kco = kco;
  },
  setKcoVersion(state) {
    state.kcoVersion = Date.now();
  },
  setAddedTaxis(state, taxis) {
    state.addedTaxis = taxis;
  },
  setSwishPaymentInProgress(state, value) {
    state.swishPaymentInProgress = value;
  },
  setSelectedPaymentType(state, value) {
    state.selectedPaymentType = value;
  },
  setHasPrintableTickets(state, value) {
    state.hasPrintableTickets = value;
  },
  setKlarnaSessionLoading(state, value) {
    state.klarnaSessionLoading = value;
  },
  setSwishSession(state, session) {
    state.swishSession = session;
    if (session) {
      localStorage.setItem(
        `swishSession_${session.orderId}`,
        JSON.stringify(session)
      );
    }
  },
  clearSwishSession(state) {
    if (state.swishSession) {
      localStorage.removeItem(`swishSession_${state.swishSession.orderId}`);
    } else if (state.orderId) {
      localStorage.removeItem(`swishSession_${state.orderId}`);
    }
    state.swishSession = null;
  },
};

vuex.actions = {
  async createAddress({ getters }, payload) {
    return await getters.accountApi.createAddress(payload);
  },

  async getTaxiOptions({ getters }, payload) {
    return await getters.api.getTaxiOptions(getters.orderId, payload);
  },

  async addGiftCards({ dispatch, getters }, { orderId, payload }) {
    await dispatch("clearSwishSession");
    return await getters.api.addGiftcards(orderId, payload);
  },

  async confirmationCheck(
    { getters },
    { pushUrl, accountId, selectedShippingSlug }
  ) {
    return await getters.api.confirmationCheck(
      pushUrl,
      accountId,
      selectedShippingSlug
    );
  },

  commitBlockingError({ commit }, error) {
    commit("setError", error);
    return error;
  },

  async updateOrder({ commit, getters }, payload) {
    const order = await getters.api.updateOrder(getters.orderId, payload);
    commitOrder(commit, order);
    return order;
  },

  async fetchOrder({ commit, getters }) {
    const order = await getters.api.getOrder(getters.orderId);
    commitOrder(commit, order);
    return order;
  },

  async getOrderConfirmation({ getters }) {
    return getters.api.getConfirmationHTML(getters.orderId);
  },

  async fetchPaymentSession(
    { commit, getters, state },
    { system, checkoutUrl, confirmationUrl }
  ) {
    let session;
    if (system === "fortnox") {
      const isBusinessOrder =
        state.data?.invoice != null &&
        Object.keys(state.data.invoice).length > 0;
      if (isBusinessOrder) {
        session = await getters.api.getPaymentSession(
          system,
          getters.orderId,
          checkoutUrl,
          confirmationUrl
        );
      } else {
        session = await getters.orderApi.getPaymentSessionInvoice(
          system,
          getters.orderId,
          checkoutUrl,
          confirmationUrl
        );
      }
    } else {
      session = await getters.api.getPaymentSession(
        system,
        getters.orderId,
        checkoutUrl,
        confirmationUrl
      );
    }
    commit("setPaymentSession", session);
    if (system === "klarna") {
      commit("setKco", session.data.snippet);
      commit("setKcoVersion");
    }
    await new Promise((resolve) => setTimeout(resolve, 300));
    return session;
  },

  async refreshKlarnaPaymentSession({ commit, dispatch, state }) {
    const { checkoutUrl, confirmationUrl } = getPaymentSessionUrls({
      orderId: state.orderId,
      token: state.token,
    });

    commit("setKlarnaSessionLoading", true);
    try {
      return await dispatch("fetchPaymentSession", {
        system: "klarna",
        checkoutUrl,
        confirmationUrl,
      });
    } finally {
      commit("setKlarnaSessionLoading", false);
    }
  },

  async validatePayment({ getters }) {
    return await getters.api.validatePayment(getters.orderId);
  },

  async updateOrderDistributions({ commit, getters }) {
    commit("setLoading", true);

    try {
      const order = await getters.api.updateOrder(getters.orderId, {
        distributions: getters.data.distributions,
      });
      commitOrder(commit, order);
      return order;
    } finally {
      commit("setLoading", false);
    }
  },

  async getRefundAmount({ getters, state }, params) {
    return await getters.api.getRefundAmount(params);
  },

  async cancelAndRefund({ getters, state }, itemId, externalReferences) {
    state.itemId = itemId;
    state.externalReferences = externalReferences;
    await getters.api.cancelAndRefund(
      getters.itemId,
      getters.externalReferences
    );
  },

  async removeItem({ dispatch, getters, state }, payload) {
    state.itemId = payload.itemIds[0];
    state.orderId = payload.orderId;
    await dispatch("clearSwishSession");
    return await getters.api.removeItem(getters.orderId, getters.itemId);
  },

  async cancelOrder({ getters, state }, itemId, externalId) {
    state.itemId = itemId;
    state.externalId = externalId;
    await getters.api.cancelOrder(getters.itemId, getters.externalId);
  },

  async resendTicket({ getters, state }, itemId) {
    state.itemId = itemId;
    return await getters.api.resendTicket(itemId);
  },
  async getFiles({ getters, state }, itemId) {
    state.itemId = itemId;
    return await getters.api.getFiles(itemId);
  },

  async validateTicket({ getters, state }, itemId) {
    state.itemId = itemId;
    return await getters.api.validateTicket(itemId);
  },

  async lockOrder({ commit, getters, state }, { reason, orderId }) {
    let orderLockExpiry = Cookies.get("_order_expiry_");
    const lastOrderId = Cookies.get("_order_id_");

    if (
      !orderLockExpiry || // if we don't have any expiry date
      moment(orderLockExpiry).isSameOrBefore(moment()) || // if expired
      lastOrderId !== orderId // if order ids don't match
    ) {
      try {
        const locks = await getters.api.lockOrder(getters.orderId, reason);

        if (!locks?.length) {
          return;
        }

        orderLockExpiry = locks.reduce((a, b) => (a.expires < b.expires ? a.expires : b.expires), 0); // prettier-ignore
        orderLockExpiry = moment.utc(orderLockExpiry).local().format("YYYY-MM-DD HH:mm:ss"); // prettier-ignore

        Cookies.set("_order_expiry_", orderLockExpiry);
        Cookies.set("_order_id_", orderId);

        commit("lockOrder");
      } catch (error) {
        if (error instanceof OrderLockException) {
          localStorage.setItem("paymentURL", window.location.href);
          router.push({ name: "LockedPage", params: { locale: "en" } });
        }

        throw error;
      }
    }

    // schedule the renewal of the order lock
    if (orderLockExpiry) {
      const expiryInMilliseconds = moment(orderLockExpiry).diff(moment());

      if (!Number.isFinite(expiryInMilliseconds) || expiryInMilliseconds <= 0) {
        return;
      }

      clearTimeout(state.orderLockSchedule);
      state.orderLockSchedule = setTimeout(() => {
        this.dispatch("order/lockOrder", {
          reason:
            "Order lock has been extended from the customer/payment page.",
          orderId: "",
        }).catch(() => {
          // lockOrder already redirects to LockedPage on failure; nothing further to do here.
        });
      }, expiryInMilliseconds);
    }
  },

  checkOrderExpire({}, callback) {
    let timer;
    clearTimeout(timer);
    const afterSeconds = moment().millisecond() + 600000;
    timer = setTimeout(() => {
      callback();
    }, afterSeconds);
  },

  async unlockOrder({ commit, getters, state }) {
    await getters.api.unlockOrder(getters.orderId);
    Cookies.remove("_order_expiry_");
    clearTimeout(state.orderLockSchedule);
    commit("orderUnlocked");
  },

  // Deliberately not part of fetchOrder: /distributions takes the same backend
  // lock as the payment complete call, so a generic order refresh must never
  // trigger it — that is what made complete and this request collide
  // (TRAIN-2238). Dispatch it only where the distribution data can actually
  // change: when the payment page opens, and when items are removed from it.
  async fetchDistributionOptions({ commit, getters }) {
    const data = await getters.api.listDistributionOptions(getters.orderId);

    if (data?.hasPrintableTickets !== undefined) {
      commit("setHasPrintableTickets", data.hasPrintableTickets);
    }

    return data;
  },

  async fetchTaxiOptions({ getters }, payload) {
    return await getters.api.getTaxiOptions(getters.orderId, payload);
  },

  async fetchTaxiPayload({ getters }) {
    return await getters.api.getTaxiPayload(getters.orderId);
  },

  async fetchLoggedTaxiPayload({ getters }, payload) {
    const orderId = getters.orderId || null;
    const tenantId = payload?.tenantId || null;
    return await getters.orderApi.getLoggedTaxiPayload(orderId, tenantId);
  },
  async addItemToOrder({ dispatch, getters }, payload) {
    await dispatch("clearSwishSession");
    return await getters.api.addItemToOrder(getters.orderId, payload);
  },

  saveSwishSession(
    { commit, getters },
    { sessionId, type, qrCode, phone, paymentToken }
  ) {
    const orderId = getters.orderId;
    if (!orderId) return;
    const session = {
      orderId,
      sessionId,
      type,
      paymentToken: paymentToken || null,
      qrCode: qrCode || null,
      phone: phone || null,
      expiresAt: Date.now() + 180 * 1000,
    };
    commit("setSwishSession", session);
  },

  restoreSwishSession({ commit, getters }) {
    const orderId = getters.orderId;
    if (!orderId) return null;
    try {
      const key = `swishSession_${orderId}`;
      let raw = localStorage.getItem(key);
      if (!raw) {
        // Fallback to old key for backwards compatibility
        const oldKey = `swishQrSession_${orderId}`;
        raw = localStorage.getItem(oldKey);
        if (raw) localStorage.removeItem(oldKey);
      }
      if (!raw) {
        return null;
      }
      const session = JSON.parse(raw);
      if (session.orderId !== orderId) {
        localStorage.removeItem(key);
        return null;
      }
      if (Date.now() >= session.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }
      if (!session.type) {
        session.type = session.qrCode ? "qr" : "send";
      }
      commit("setSwishSession", session);
      return session;
    } catch (e) {
      localStorage.removeItem(`swishSession_${orderId}`);
      return null;
    }
  },

  clearSwishSession({ commit, getters }) {
    commit("clearSwishSession");
    commit("setSwishPaymentInProgress", false);
  },
};

export default {
  namespaced: true,
  state: vuex.state,
  getters: vuex.getters,
  mutations: vuex.mutations,
  actions: vuex.actions,
};
